import { UserClaims } from './user';
import { Links } from '@shared/constants/links';
import { PatientListTabIds } from '@shared/types/patientListTypes';

try {
  const testKey = '__storage_test__';
  window.localStorage.setItem(testKey, testKey);
  window.localStorage.removeItem(testKey);
} catch (e) {
  console.error('localStorage is not available. Redirecting to not-supported page.');
  window.location.replace(Links.notSupported);
}

const storage = window.localStorage;

export const keys = {
  authCodeState: 'authCodeState',
  authCodeVerifier: 'authCodeVerifier',
  lastActionTime: 'lastTimeActive',
  userName: 'userName',
  authTimeLocal: 'authTimeLocal',
  refreshAuthData: 'refreshAuthData',
  culture: 'culture',
  email: 'email',
  activeMonitoringTabId: 'activeMonitoringTabId',
  hiddenMaintenanceNotificationIds: 'hiddenMaintenanceNotificationIds',
  hiddenExternalNotificationIds: 'hiddenExternalNotificationIds',
  delayedExternalNotifications: 'delayedExternalNotifications',
  userClaims: 'userClaims',
} as const;

export const getStorageItem = <T>(key: keyof typeof keys) => {
  const content = storage.getItem(key);
  return content ? (JSON.parse(content) as T) : null;
};

export const getAuthCodeState = () => storage.getItem(keys.authCodeState);
export const getAuthCodeVerifier = () => storage.getItem(keys.authCodeVerifier);
export const getName = (): string | null => storage.getItem(keys.userName);
export const getCulture = (): string | null => storage.getItem(keys.culture);
export const getLastActive = (): string | null => storage.getItem(keys.lastActionTime);
export const getAuthTimeLocal = (): string | null => storage.getItem(keys.authTimeLocal);
export const getUserClaims = (): string | null => storage.getItem(keys.userClaims);

export const getActiveMonitoringTabId = (): PatientListTabIds | null => {
  const storedActiveTab = storage.getItem(keys.activeMonitoringTabId);

  if (Object.keys(PatientListTabIds).includes(storedActiveTab as PatientListTabIds)) {
    return storedActiveTab as PatientListTabIds;
  }

  return null;
};

export const getHiddenMaintenanceNotificationIds = (): string[] | null =>
  storage.getItem(keys.hiddenMaintenanceNotificationIds)?.split(',') ?? null;

export type DelayedExternalNotification = { id: string; delayedUntilMsTimestamp: number };
export const getDelayedExternalNotifications = (): DelayedExternalNotification[] | null => {
  const items = storage.getItem(keys.delayedExternalNotifications);
  return items ? JSON.parse(items) : null;
};
export const getHiddenExternalNotificationIds = (): string[] | null => {
  const ids = storage.getItem(keys.hiddenExternalNotificationIds);
  return ids ? JSON.parse(ids) : null;
};

export const subscribeAuthTimeLocalChanged = (callback: () => void) => {
  window.addEventListener('storage', (event) => {
    if (keys.authTimeLocal === event.key) {
      callback();
    }
  });
};

export const clearUserState = (): void => {
  storage.removeItem(keys.userName);
  storage.removeItem(keys.lastActionTime);
  storage.removeItem(keys.authTimeLocal);
  storage.removeItem(keys.email);
  storage.removeItem(keys.hiddenMaintenanceNotificationIds);
  storage.removeItem(keys.authCodeState);
  storage.removeItem(keys.authCodeVerifier);
  storage.removeItem(keys.userClaims);
};

export const setStorageItem = <T>(key: keyof typeof keys, value: T) => storage.setItem(key, JSON.stringify(value));

export const setUserState = (userName: string, userClaims: UserClaims): void => {
  storage.setItem(keys.userName, userName);
  storage.setItem(keys.userClaims, JSON.stringify(userClaims));
  storage.removeItem(keys.authCodeState);
  storage.removeItem(keys.authCodeVerifier);
};

export const setAuthenticationState = (state: string, codeVerifier: string) => {
  storage.setItem(keys.authCodeState, state);
  storage.setItem(keys.authCodeVerifier, codeVerifier);
};

export const setAuthTimeLocal = (time: Date): void => storage.setItem(keys.authTimeLocal, time.toISOString());

export interface CultureStorageObject {
  culture: string;
  dayJSLocale: string;
  isCultureUserDefined: boolean;
}

export const setCulture = (culture: string, dayJSLocale: string, isCultureUserDefined: boolean): void => {
  const object: CultureStorageObject = { culture, isCultureUserDefined, dayJSLocale };
  storage.setItem(keys.culture, JSON.stringify(object));
};

export const setDelayedExternalNotifications = (delayedNotifications: DelayedExternalNotification[]): void =>
  storage.setItem(keys.delayedExternalNotifications, JSON.stringify(delayedNotifications));

export const setActive = (): void => storage.setItem(keys.lastActionTime, Date.now().toString());

export const setActiveMonitoringTabId = (tabId: PatientListTabIds) =>
  storage.setItem(keys.activeMonitoringTabId, tabId);

export const setHiddenMaintenanceNotifications = (ids: string[]) => {
  storage.setItem(keys.hiddenMaintenanceNotificationIds, ids.join());
};

export const setHiddenExternalNotifications = (ids: string[]) =>
  storage.setItem(keys.hiddenExternalNotificationIds, JSON.stringify(ids));

export const subscribeToLocaleChange = (callback: (newLocale: CultureStorageObject) => void) => {
  window.addEventListener('storage', (event) => {
    if (event.key === keys.culture && event.newValue) {
      const newLocale = JSON.parse(event.newValue) as CultureStorageObject;
      callback(newLocale);
    }
  });
};

export const subscribeToLogout = (callback: () => void) => {
  window.addEventListener('storage', (event) => {
    if (event.key === keys.userClaims && event.newValue === null) {
      callback();
    }
  });
};
